import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type SetStateAction,
} from 'react';

import {
  Achievement,
  AchievementSaveData,
  AchievementContext,
} from './achievements';
import { getAssemblerCenterPosition } from './BeltItemSystem';
import {
  calculateInitialGalaxyState,
  type GalaxyState,
} from './components/SpaceCivMode';
import {
  GAME_CONFIG,
  SAVE_CONFIG,
  BUILDINGS,
  BUILDINGS_BY_ID,
  Position,
  Building,
  BuildingType,
  BuildingCategory,
  BuildMenuState,
  BeltType,
  BELT_CHARACTERS,
  FAST_BELT_CHARACTERS,
  BeltItem,
  AssemblerInventory,
  Recipe,
  ItemType,
  ChestInventory,
  getItemStackSize,
  ITEM_TYPES,
  ITEMS,
  createEmptyItemCounts,
  PLAYER_INVENTORY_SLOT_COUNT,
  ResearchId,
  RESEARCHES,
  BuildingItemRequirement,
} from './constants';
import {
  GameEngine,
  CoreGameState,
  getGameEngine,
  GameEngineEventCallback,
  UpgradeAssemblerResult,
  QuickBeltPlacementResult,
  QuickBeltPreviewState,
} from './GameEngine';
import {
  HandCraftingQueueEntry,
  normalizeHandCraftingQueue,
} from './HandCrafting';
import { DebugState } from './hooks/useDebugState';
import { useTickSubscription, getCurrentTick } from './hooks/useGameTick';
import { saveLogger, buildingLogger } from './Logger';
import {
  PrestigeUpgradeState,
  SerializedPrestigeUpgradeState,
  serializePrestigeUpgradeState,
  PrestigeUpgradeId,
} from './PrestigeSystem';
import {
  ResearchState,
  ResearchProgress,
  serializeResearchState,
  serializeResearchProgress,
  deserializeResearchState,
  serializeRepeatableResearchLevels,
  RepeatableResearchLevels,
  SerializedResearchState,
  isRecipeUnlocked,
  getRequiredResearchForBuilding,
  getOutputCapacityMultiplier,
} from './ResearchSystem';
import {
  processSmartBeltPlacement,
  getBeltDirectionFromCharacter,
  getBeltTypeFromCharacter,
} from './SmartBeltPlacement';
import { TICK_INTERVALS, secondsToTicks } from './utils/TickIntervals';

// Game map type - using string keys for position coordinates
export type GameMap = Record<string, string>;

// Building layer type - separate from terrain modifications
export type BuildingLayer = Record<string, string>;

// Belt content layer type - tracks items on belts
export type BeltContentLayer = Record<string, BeltItem[]>;

// Assembler inventory layer type - tracks items and recipes in assemblers
export type AssemblerInventoryLayer = Record<string, AssemblerInventory>;

// Chest inventory layer type - tracks buffered items in storage buildings
export type ChestInventoryLayer = Record<string, ChestInventory>;

export interface PlayerInventorySlot {
  itemType: ItemType | null;
  count: number;
}

export type PlayerInventory = PlayerInventorySlot[];

// Rejected items layer type - tracks visual feedback for rejected items
export interface RejectedItem {
  readonly timestamp: number; // When the rejection occurred (game ticks)
  readonly itemType: ItemType; // What item was rejected
}
export type RejectedItemsLayer = Record<string, RejectedItem>;

// Miner timer layer type - tracks when miners last output items
export type MinerTimerLayer = Record<string, number>; // position -> last output timestamp
export type MinerOutputBufferLayer = Record<string, number>; // position -> buffered miner items

export interface ResourceNodeMenuState {
  readonly position: Position;
  readonly resourceItemType: ItemType;
  readonly resourceCharacter: string;
}

export interface MineResourceResult {
  readonly success: boolean;
  readonly itemType?: ItemType;
  readonly added?: number;
  readonly reason?: 'no-resource' | 'inventory-full';
}

export interface CollectBuildingInventoryResult {
  readonly success: boolean;
  readonly addedItems: Partial<Record<ItemType, number>>;
  readonly partial?: boolean;
  readonly reason?:
    | 'invalid-building'
    | 'no-inventory'
    | 'no-items'
    | 'inventory-full'
    | 'not-ready';
}

export type PowerDemandByBuildingType = Partial<Record<BuildingType, number>>;

export interface PowerSnapshot {
  readonly tick: number;
  readonly production: number;
  readonly demand: number;
  readonly satisfaction: number;
  readonly demandByBuildingType: PowerDemandByBuildingType;
}

export interface ItemProductionHistoryEntry {
  readonly tick: number;
  readonly amounts: Partial<Record<ItemType, number>>;
}

// Save data interface
export interface SaveData {
  version: string;
  timestamp: number;
  playerPos: Position;
  playerInventory?: PlayerInventory;
  gameMap: GameMap;
  buildingLayer: BuildingLayer;
  beltContentLayer: BeltContentLayer;
  assemblerInventoryLayer: AssemblerInventoryLayer;
  chestInventoryLayer: ChestInventoryLayer;
  rejectedItemsLayer: RejectedItemsLayer;
  minerTimerLayer: MinerTimerLayer;
  minerOutputBufferLayer: MinerOutputBufferLayer;
  prestigePoints: number;
  prestigeCount: number;
  cameraPos: Position;
  currentBiome: string;
  playerFacing: PlayerFacing;
  zoomLevel: number;
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;
  helpMenuOpen: boolean;
  saveMenuOpen: boolean;
  leftPanelOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  researchMenuOpen: boolean;
  buildingStatusMenuTarget?: { x: number; y: number } | null;
  hasEnabledDebug: boolean;
  achievements: AchievementSaveData;
  unlockedResearch: ResearchId[];
  selectedResearch: ResearchId | null;
  researchQueue?: ResearchId[];
  researchProgress: ResearchProgress;
  repeatableResearchLevels?: RepeatableResearchLevels;
  tickSpeedMultiplier?: number;
  powerProduction?: number;
  powerDemand?: number;
  powerSatisfaction?: number;
  powerHistory?: PowerSnapshot[];
  powerDemandByBuildingType?: PowerDemandByBuildingType;
  productionMenuOpen?: boolean;
  itemProductionHistory?: ItemProductionHistoryEntry[];
  currentTick?: number;
  spaceGalaxyState?: GalaxyState;
  prestigeUpgradeState?: SerializedPrestigeUpgradeState;
  handCraftingQueue?: HandCraftingQueueEntry[];
}

// Player facing direction type
export type PlayerFacing = 'up' | 'down' | 'left' | 'right';

// Game state interface
export interface GameState {
  isLoading: boolean;
  playerPos: Position;
  playerInventory: PlayerInventory;
  gameMap: GameMap;
  buildingLayer: BuildingLayer;
  beltContentLayer: BeltContentLayer;
  assemblerInventoryLayer: AssemblerInventoryLayer;
  chestInventoryLayer: ChestInventoryLayer;
  rejectedItemsLayer: RejectedItemsLayer;
  minerTimerLayer: MinerTimerLayer;
  minerOutputBufferLayer: MinerOutputBufferLayer;
  prestigePoints: number;
  prestigeCount: number;
  cameraPos: Position;
  currentBiome: string;
  playerFacing: PlayerFacing;
  zoomLevel: number;
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;
  helpMenuOpen: boolean;
  saveMenuOpen: boolean;
  leftPanelOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  researchMenuOpen: boolean;
  buildingStatusMenuTarget: { x: number; y: number } | null;
  resourceNodeMenu: ResourceNodeMenuState | null;
  hasEnabledDebug: boolean;
  achievements: Achievement[];
  researchState: ResearchState;
  tickSpeedMultiplier: number;
  powerProduction: number;
  powerDemand: number;
  powerSatisfaction: number;
  powerHistory: PowerSnapshot[];
  powerDemandByBuildingType: PowerDemandByBuildingType;
  productionMenuOpen: boolean;
  itemProductionHistory: ItemProductionHistoryEntry[];
  currentTick: number;
  handCraftingQueue: HandCraftingQueueEntry[];
  spaceGalaxyState: GalaxyState;
  prestigeMenuOpen: boolean;
  prestigeUpgradeState: PrestigeUpgradeState;
  quickBeltPreview: QuickBeltPreviewState | null;
}

// Function type for getTile helper
export type GetTileFunction = (
  x: number,
  y: number,
  gameMap: GameMap,
  buildingLayer?: BuildingLayer
) => string | undefined;

// Function type for preGenerateArea helper
export type PreGenerateAreaFunction = (
  x: number,
  y: number,
  radius: number
) => void;

// Game actions interface
export interface GameActions {
  movePlayer: (newX: number, newY: number) => void;
  collectResource: (x: number, y: number) => void;
  collectBuildingInventory: (
    position: Position,
    itemType?: ItemType
  ) => CollectBuildingInventoryResult;
  addItemToInventory: (itemType: ItemType, amount: number) => number;
  queueHandCrafting: (itemType: ItemType, amount: number) => boolean;
  updatePlayerInventory: (
    updater: (inventory: PlayerInventory) => PlayerInventory
  ) => void;
  updateTile: (x: number, y: number, character: string) => void;
  startQuickBeltPlacement: (beltType: BeltType, position: Position) => void;
  extendQuickBeltPlacement: (position: Position) => void;
  finalizeQuickBeltPreview: () => void;
  cancelQuickBeltPlacement: () => void;
  confirmQuickBeltPlacement: () => QuickBeltPlacementResult;
  placeBuilding: (
    x: number,
    y: number,
    building: Building,
    getTile?: GetTileFunction,
    preGenerateArea?: PreGenerateAreaFunction,
    showError?: (message: string) => void
  ) => boolean;
  deconstructBuilding: (
    x: number,
    y: number,
    showError?: (message: string) => void
  ) => boolean;
  upgradeAssemblerToFactory: (position: Position) => UpgradeAssemblerResult;
  updateBiome: (biomeName: string) => void;
  getTargetPosition: (building?: Building) => Position;
  getBuildingTargetLocation: (building: Building) => Position;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  saveGame: () => boolean;
  resetGame: () => void;
  loadGameFromSaveData: (saveData: SaveData) => void;
  launchRocket: () => void;
  setHasEnabledDebug: (enabled: boolean) => void;
  getAchievements: () => Achievement[];
  triggerAchievementCheck: (context: AchievementContext) => void;
  setBuildMenuState: (updates: {
    buildMenuOpen?: BuildMenuState;
    selectedCategory?: BuildingCategory | null;
    selectedBuilding?: Building | null;
  }) => void;
  setBuildingStatusMenuTarget: (
    target: { x: number; y: number } | null
  ) => void;
  openBuildingStatusMenu: (position: { x: number; y: number }) => void;
  closeBuildingStatusMenu: () => void;
  openResourceNodeMenu: (position: { x: number; y: number }) => void;
  closeResourceNodeMenu: () => void;
  mineResourceNode: (position: { x: number; y: number }) => MineResourceResult;
  unlockResearch: (researchId: ResearchId) => void;
  forceUnlockResearch: (researchId: ResearchId) => void;
  isResearchUnlocked: (researchId: ResearchId) => boolean;
  getUnlockedResearchIds: () => ResearchId[];
  selectResearch: (researchId: ResearchId | null) => void;
  setResearchMenuOpen: (open: boolean) => void;
  setProductionMenuOpen: (open: boolean) => void;
  resetResearch: (researchId: ResearchId) => void;
  resetAllResearch: () => void;
  setTickSpeedMultiplier: (multiplier: number) => void;
  cycleTickSpeedMultiplier: () => number;
  setSpaceGalaxyState: React.Dispatch<React.SetStateAction<GalaxyState>>;
  forcePrestige: (count: number) => void;
  selectPrestigeUpgrade: (upgradeId: PrestigeUpgradeId | null) => void;
  purchasePrestigeUpgrade: (upgradeId: PrestigeUpgradeId) => boolean;
  setPrestigeMenuOpen: (open: boolean) => void;
}

// Complete game state hook return type
export interface UseGameStateReturn extends GameState, GameActions {
  // Raw setters for compatibility
  setGameMap: React.Dispatch<React.SetStateAction<GameMap>>;
  setBuildingLayer: React.Dispatch<React.SetStateAction<BuildingLayer>>;
  setBeltContentLayer: React.Dispatch<React.SetStateAction<BeltContentLayer>>;
  setAssemblerInventoryLayer: React.Dispatch<
    React.SetStateAction<AssemblerInventoryLayer>
  >;
  setChestInventoryLayer: React.Dispatch<
    React.SetStateAction<ChestInventoryLayer>
  >;
  setRejectedItemsLayer: React.Dispatch<
    React.SetStateAction<RejectedItemsLayer>
  >;
  setMinerTimerLayer: React.Dispatch<React.SetStateAction<MinerTimerLayer>>;
  setMinerOutputBufferLayer: React.Dispatch<
    React.SetStateAction<MinerOutputBufferLayer>
  >;
  setCurrentBiome: React.Dispatch<React.SetStateAction<string>>;
  setPlayerFacing: React.Dispatch<React.SetStateAction<PlayerFacing>>;
  setZoomLevel: React.Dispatch<React.SetStateAction<number>>;

  // GameEngine's tile lookup method - single source of truth
  getTile: (
    x: number,
    y: number,
    gameMap?: GameMap,
    buildingLayer?: BuildingLayer
  ) => string | undefined;
}

const getIsDocumentFocused = (): boolean => {
  if (typeof document === 'undefined') {
    return true;
  }

  const hasFocus =
    typeof document.hasFocus === 'function' ? document.hasFocus() : true;

  return document.visibilityState === 'visible' && hasFocus;
};

// Custom hook for managing game state - now uses GameEngine
export const useGameState = (debugState: DebugState): UseGameStateReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Create or get the game engine instance
  const engineRef = useRef<GameEngine | null>(null);
  if (!engineRef.current) {
    engineRef.current = getGameEngine();
  }
  const engine = engineRef.current;

  // React state that mirrors the game engine state for UI updates
  const [engineState, setEngineState] = useState<CoreGameState>(() =>
    engine.getState()
  );

  const [spaceGalaxyState, setSpaceGalaxyState] = useState<GalaxyState>(() =>
    calculateInitialGalaxyState()
  );

  // Subscribe to engine state changes
  useEffect(() => {
    const handleStateChanged: GameEngineEventCallback = newState => {
      setEngineState({ ...newState });
    };

    engine.addEventListener('stateChanged', handleStateChanged);
    engine.addEventListener('hoverTargetChanged', handleStateChanged);

    return () => {
      engine.removeEventListener('stateChanged', handleStateChanged);
      engine.removeEventListener('hoverTargetChanged', handleStateChanged);
    };
  }, [engine]);

  useEffect(() => {
    const debugEnabled = engineState.hasEnabledDebug;
    const shouldIgnoreCosts = debugEnabled && debugState.ignoreBuildingCosts;
    if (engine.isIgnoringBuildingCosts() !== shouldIgnoreCosts) {
      engine.setIgnoreBuildingCosts(shouldIgnoreCosts);
    }

    const shouldIgnorePower = debugEnabled && debugState.ignorePowerCosts;
    if (engine.isUnlimitedPowerModeEnabled() !== shouldIgnorePower) {
      engine.setUnlimitedPowerMode(shouldIgnorePower);
    }
  }, [
    engine,
    engineState.hasEnabledDebug,
    debugState.ignoreBuildingCosts,
    debugState.ignorePowerCosts,
  ]);

  // Tick subscription for auto-save and game engine processing
  const { subscribe, unsubscribe } = useTickSubscription();
  const isTabFocusedRef = useRef<boolean>(getIsDocumentFocused());

  const applySaveData = useCallback(
    (savedGame: SaveData): void => {
      const adjustedAssemblerInventoryLayer: AssemblerInventoryLayer = {};
      const currentTick = getCurrentTick();

      Object.entries(savedGame.assemblerInventoryLayer || {}).forEach(
        ([key, inventory]) => {
          if (
            inventory.craftingStartTick !== null &&
            inventory.craftingProgress > 0
          ) {
            const progressMade = inventory.craftingProgress;
            const recipe = inventory.recipe;

            if (recipe) {
              const totalDurationTicks = secondsToTicks(recipe.craftingTime);
              const ticksElapsed = Math.floor(
                progressMade * totalDurationTicks
              );

              adjustedAssemblerInventoryLayer[key] = {
                ...inventory,
                craftingStartTick: currentTick - ticksElapsed,
              };
            } else {
              adjustedAssemblerInventoryLayer[key] = {
                ...inventory,
                craftingStartTick: null,
                craftingProgress: 0,
              };
            }
          } else {
            adjustedAssemblerInventoryLayer[key] = inventory;
          }
        }
      );

      const normalizedHandCraftingQueue = normalizeHandCraftingQueue(
        savedGame.handCraftingQueue ?? []
      );

      const serializedResearchState = {
        unlockedResearch: savedGame.unlockedResearch,
        selectedResearch: savedGame.selectedResearch ?? null,
        queue: savedGame.researchQueue,
        progress: savedGame.researchProgress ?? {},
        ...(savedGame.repeatableResearchLevels
          ? { repeatableResearchLevels: savedGame.repeatableResearchLevels }
          : {}),
      } as SerializedResearchState;

      const canonicalSelectedBuilding = savedGame.selectedBuilding?.id
        ? (BUILDINGS_BY_ID[savedGame.selectedBuilding.id as BuildingType] ??
          null)
        : null;

      const savedPowerHistory = savedGame.powerHistory ?? [
        {
          tick: currentTick,
          production: 0,
          demand: 0,
          satisfaction: 1,
          demandByBuildingType: {},
        },
      ];

      const initialState: Partial<CoreGameState> = {
        playerPos: savedGame.playerPos,
        playerInventory: normalizePlayerInventory(savedGame.playerInventory),
        gameMap: savedGame.gameMap || {},
        buildingLayer: savedGame.buildingLayer || {},
        beltContentLayer: savedGame.beltContentLayer || {},
        assemblerInventoryLayer: adjustedAssemblerInventoryLayer,
        chestInventoryLayer: savedGame.chestInventoryLayer || {},
        rejectedItemsLayer: savedGame.rejectedItemsLayer || {},
        minerTimerLayer: savedGame.minerTimerLayer || {},
        minerOutputBufferLayer: savedGame.minerOutputBufferLayer || {},
        prestigePoints: savedGame.prestigePoints ?? 0,
        prestigeCount: savedGame.prestigeCount ?? 0,
        cameraPos: savedGame.cameraPos || savedGame.playerPos,
        currentBiome: savedGame.currentBiome || 'Plains',
        playerFacing: savedGame.playerFacing || 'down',
        zoomLevel: savedGame.zoomLevel || 2,
        hasEnabledDebug: savedGame.hasEnabledDebug || false,
        researchState: deserializeResearchState(serializedResearchState),
        tickSpeedMultiplier: savedGame.tickSpeedMultiplier ?? 1,
        powerProduction: savedGame.powerProduction ?? 0,
        powerDemand: savedGame.powerDemand ?? 0,
        powerSatisfaction: savedGame.powerSatisfaction ?? 1,
        powerHistory: savedPowerHistory.map(entry => ({
          tick: entry.tick,
          production: entry.production,
          demand: entry.demand,
          satisfaction: entry.satisfaction,
          demandByBuildingType: entry.demandByBuildingType ?? {},
        })),
        powerDemandByBuildingType: savedGame.powerDemandByBuildingType ?? {},
        productionMenuOpen: savedGame.productionMenuOpen ?? false,
        itemProductionHistory: savedGame.itemProductionHistory ?? [],
        currentTick: savedGame.currentTick ?? currentTick,
        handCraftingQueue: normalizedHandCraftingQueue,
        buildMenuOpen: savedGame.buildMenuOpen ?? false,
        selectedCategory: savedGame.selectedCategory ?? null,
        selectedBuilding: canonicalSelectedBuilding,
        helpMenuOpen: savedGame.helpMenuOpen ?? false,
        saveMenuOpen: savedGame.saveMenuOpen ?? false,
        leftPanelOpen: savedGame.leftPanelOpen ?? false,
        debugMenuOpen: savedGame.debugMenuOpen ?? false,
        achievementMenuOpen: savedGame.achievementMenuOpen ?? false,
        researchMenuOpen: savedGame.researchMenuOpen ?? false,
      };

      engine.setState(initialState);
      engine.loadAchievements(savedGame.achievements ?? {});
      setEngineState(engine.getState());

      if (savedGame.spaceGalaxyState) {
        setSpaceGalaxyState(savedGame.spaceGalaxyState);
      } else {
        setSpaceGalaxyState(calculateInitialGalaxyState());
      }
    },
    [engine, setSpaceGalaxyState]
  );

  // Load saved game on initialization
  useEffect(() => {
    const savedGame = loadGameState();
    if (savedGame) {
      applySaveData(savedGame);
    }
    setIsLoading(false);
  }, [applySaveData]);

  // Manual save function
  const saveGame = useCallback((): boolean => {
    const currentEngineState = engine.getState();
    const canonicalSelectedBuilding = currentEngineState.selectedBuilding
      ? BUILDINGS_BY_ID[currentEngineState.selectedBuilding.id as BuildingType]
      : null;
    const currentState: Omit<SaveData, 'version' | 'timestamp'> = {
      playerPos: currentEngineState.playerPos,
      playerInventory: currentEngineState.playerInventory,
      gameMap: currentEngineState.gameMap,
      buildingLayer: currentEngineState.buildingLayer,
      beltContentLayer: currentEngineState.beltContentLayer,
      assemblerInventoryLayer: currentEngineState.assemblerInventoryLayer,
      chestInventoryLayer: currentEngineState.chestInventoryLayer,
      rejectedItemsLayer: currentEngineState.rejectedItemsLayer,
      minerTimerLayer: currentEngineState.minerTimerLayer,
      minerOutputBufferLayer: currentEngineState.minerOutputBufferLayer,
      prestigePoints: currentEngineState.prestigePoints,
      prestigeCount: currentEngineState.prestigeCount,
      cameraPos: currentEngineState.cameraPos,
      currentBiome: currentEngineState.currentBiome,
      playerFacing: currentEngineState.playerFacing,
      zoomLevel: currentEngineState.zoomLevel,
      buildMenuOpen: currentEngineState.buildMenuOpen,
      selectedCategory: currentEngineState.selectedCategory,
      selectedBuilding: canonicalSelectedBuilding,
      helpMenuOpen: currentEngineState.helpMenuOpen,
      saveMenuOpen: currentEngineState.saveMenuOpen,
      leftPanelOpen: currentEngineState.leftPanelOpen,
      debugMenuOpen: currentEngineState.debugMenuOpen,
      achievementMenuOpen: currentEngineState.achievementMenuOpen,
      researchMenuOpen: currentEngineState.researchMenuOpen,
      productionMenuOpen: currentEngineState.productionMenuOpen,
      buildingStatusMenuTarget: null, // Don't persist transient UI state
      hasEnabledDebug: currentEngineState.hasEnabledDebug,
      achievements: currentEngineState.achievementSaveData || {},
      unlockedResearch: serializeResearchState(
        currentEngineState.researchState
      ),
      selectedResearch: currentEngineState.researchState.selectedResearch,
      researchQueue: currentEngineState.researchState.queue,
      researchProgress: serializeResearchProgress(
        currentEngineState.researchState
      ),
      tickSpeedMultiplier: currentEngineState.tickSpeedMultiplier,
      powerProduction: currentEngineState.powerProduction,
      powerDemand: currentEngineState.powerDemand,
      powerSatisfaction: currentEngineState.powerSatisfaction,
      powerHistory: currentEngineState.powerHistory,
      powerDemandByBuildingType: currentEngineState.powerDemandByBuildingType,
      itemProductionHistory: currentEngineState.itemProductionHistory,
      currentTick: currentEngineState.currentTick,
      spaceGalaxyState,
      prestigeUpgradeState: serializePrestigeUpgradeState(
        currentEngineState.prestigeUpgradeState
      ),
      ...(() => {
        const repeatableLevels = serializeRepeatableResearchLevels(
          currentEngineState.researchState
        );
        return Object.keys(repeatableLevels).length > 0
          ? { repeatableResearchLevels: repeatableLevels }
          : {};
      })(),
    };
    return saveGameState(currentState);
  }, [engine, spaceGalaxyState]);

  // Auto-save functionality and main game tick processing
  useEffect(() => {
    const updateTabFocus = (): void => {
      const isFocused = getIsDocumentFocused();
      isTabFocusedRef.current = isFocused;
      engine.setAudioSuspended(!isFocused);
    };

    updateTabFocus();

    const autoSaveSubscriptionId = subscribe(() => {
      if (isTabFocusedRef.current) {
        saveGame();
      }
    }, TICK_INTERVALS.AUTO_SAVE); // Every 1800 ticks (30 seconds at 60 FPS)

    // Main game tick subscription - this drives all game logic
    const gameTickSubscriptionId = subscribe(() => {
      const engineState = engine.getState();
      const hasEnabledDebug = engineState.hasEnabledDebug;
      const multiplier = hasEnabledDebug
        ? Math.max(1, engineState.tickSpeedMultiplier ?? 1)
        : 1;

      for (let i = 0; i < multiplier; i++) {
        engine.processTick();
      }
    }, 1); // Every tick (60 FPS)

    // Save on page unload
    const handleBeforeUnload = (): void => {
      if (SAVE_CONFIG.SAVE_ON_UNLOAD) {
        saveGame();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', updateTabFocus);
    window.addEventListener('focus', updateTabFocus);
    window.addEventListener('blur', updateTabFocus);

    return () => {
      unsubscribe(autoSaveSubscriptionId);
      unsubscribe(gameTickSubscriptionId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', updateTabFocus);
      window.removeEventListener('focus', updateTabFocus);
      window.removeEventListener('blur', updateTabFocus);
    };
  }, [saveGame, subscribe, unsubscribe, engine]);

  // Game state actions - now delegate to engine
  const actions: GameActions = {
    // Player movement
    movePlayer: (newX: number, newY: number): void => {
      engine.movePlayer(newX, newY);
    },

    // Resource management
    collectResource: (x: number, y: number): void => {
      engine.collectResource(x, y);
    },

    collectBuildingInventory: (
      position: Position,
      itemType?: ItemType
    ): CollectBuildingInventoryResult => {
      return engine.collectBuildingInventory(position, itemType);
    },

    addItemToInventory: (itemType: ItemType, amount: number): number => {
      return engine.addItemToInventory(itemType, amount);
    },

    queueHandCrafting: (itemType: ItemType, amount: number): boolean => {
      return engine.queueHandCrafting(itemType, amount);
    },

    updatePlayerInventory: (
      updater: (inventory: PlayerInventory) => PlayerInventory
    ): void => {
      const currentInventory = engine.getState().playerInventory;
      const nextInventory = updater(
        currentInventory.map(slot => ({ ...slot }))
      );

      if (!Array.isArray(nextInventory)) {
        buildingLogger.warn('updatePlayerInventory received invalid result');
        return;
      }

      engine.setState({ playerInventory: nextInventory });
    },

    // Map updates
    updateTile: (x: number, y: number, character: string): void => {
      engine.updateTile(x, y, character);
    },

    startQuickBeltPlacement: (beltType: BeltType, position: Position): void => {
      engine.startQuickBeltPlacement(beltType, position);
    },

    extendQuickBeltPlacement: (position: Position): void => {
      engine.extendQuickBeltPlacement(position);
    },

    finalizeQuickBeltPreview: (): void => {
      engine.finalizeQuickBeltPreview();
    },

    cancelQuickBeltPlacement: (): void => {
      engine.cancelQuickBeltPlacement();
    },

    confirmQuickBeltPlacement: (): QuickBeltPlacementResult => {
      return engine.confirmQuickBeltPlacement();
    },

    // Building placement
    placeBuilding: (
      x: number,
      y: number,
      building: Building,
      getTile?: GetTileFunction,
      preGenerateArea?: PreGenerateAreaFunction,
      showError?: (message: string) => void
    ): boolean => {
      const currentState = engine.getState();

      const requiredResearchId = getRequiredResearchForBuilding(building.id);
      if (
        requiredResearchId &&
        !engine.isResearchUnlocked(requiredResearchId)
      ) {
        const research = RESEARCHES[requiredResearchId];
        const message = research
          ? `${building.name} requires ${research.name} research`
          : `${building.name} requires prior research`;
        buildingLogger.warn('Building placement blocked - research locked', {
          building: building.id,
          requiredResearchId,
        });
        showError?.(message);
        return false;
      }

      const itemRequirements = building.cost.items ?? [];
      let chestInventoryLayerAfterCost = currentState.chestInventoryLayer;
      let chestInventoryChanged = false;
      let playerInventoryAfterCost = currentState.playerInventory;
      let playerInventoryChanged = false;
      let itemsReserved = false;
      const ignoreBuildingCosts = engine.isIgnoringBuildingCosts();

      if (ignoreBuildingCosts) {
        itemsReserved = true;
      }

      const reserveItems = (): boolean => {
        if (ignoreBuildingCosts) {
          buildingLogger.info('Skipping building cost due to debug setting', {
            building: building.id,
          });
          return true;
        }

        if (itemRequirements.length === 0) {
          return true;
        }

        const availableItems = getAvailableItemCountsForPlacement(
          currentState.playerInventory,
          currentState.chestInventoryLayer
        );
        const missingRequirement = itemRequirements.find(
          ({ item, amount }) => (availableItems[item] ?? 0) < amount
        );

        if (missingRequirement) {
          const itemConfig = Object.values(ITEMS).find(
            candidate => candidate.type === missingRequirement.item
          );
          const itemName = itemConfig?.name ?? missingRequirement.item;
          const availableAmount = availableItems[missingRequirement.item] ?? 0;

          buildingLogger.warn('Not enough items to place building', {
            required: missingRequirement,
            available: availableAmount,
            building: building.name,
          });

          showError?.(
            `Not enough ${itemName}! Need ${missingRequirement.amount}, have ${availableAmount}`
          );
          return false;
        }

        const consumptionResult = consumeItemsForBuilding(
          currentState.playerInventory,
          currentState.chestInventoryLayer,
          itemRequirements
        );

        if (!consumptionResult.success) {
          buildingLogger.warn(
            'Failed to reserve item requirements for building',
            {
              building: building.name,
              requirements: itemRequirements,
            }
          );

          showError?.(
            'Unable to reserve required items for building placement'
          );
          return false;
        }

        chestInventoryLayerAfterCost = consumptionResult.chestInventoryLayer;
        chestInventoryChanged = consumptionResult.chestInventoryChanged;
        playerInventoryAfterCost = consumptionResult.playerInventory;
        playerInventoryChanged = consumptionResult.playerInventoryChanged;
        itemsReserved = true;
        return true;
      };

      // Ensure the area around the target position is generated
      if (preGenerateArea) {
        preGenerateArea(x, y, 5);
      }

      // Get all positions that this building will occupy
      const buildingPositions = getBuildingPositions(x, y, building);
      const isBelt =
        building.id === BuildingType.BELT ||
        building.id === BuildingType.FAST_BELT;

      // For single-tile buildings, handle belt rotation special case
      if (buildingPositions.length === 1 && isBelt) {
        const pos = buildingPositions[0];
        if (!pos) {
          throw new Error('Expected building position but none found');
        }
        const posKey = `${pos.x},${pos.y}`;
        const existingBuilding = currentState.buildingLayer[posKey];

        if (existingBuilding) {
          // Check if existing building is also a belt
          const existingBeltDirection = getBeltDirectionFromCharacter(
            currentState.buildingLayer,
            pos
          );
          const existingBeltType = getBeltTypeFromCharacter(existingBuilding);

          if (existingBeltDirection && existingBeltType) {
            // Check if it's the same belt type or allow upgrading/downgrading
            const newBuildingCharacter = getDirectionalBuildingCharacter(
              building,
              currentState.playerFacing
            );

            if (existingBeltType !== building.id) {
              if (!reserveItems()) {
                return false;
              }
            }

            // Rotate/change the belt
            engine.setState({
              buildingLayer: {
                ...currentState.buildingLayer,
                [posKey]: newBuildingCharacter,
              },
            });

            if (playerInventoryChanged) {
              engine.setState({ playerInventory: playerInventoryAfterCost });
            }
            if (chestInventoryChanged) {
              engine.setState({
                chestInventoryLayer: chestInventoryLayerAfterCost,
              });
            }

            buildingLogger.info('Belt rotated/changed', {
              position: { x: pos.x, y: pos.y },
              from: {
                type: existingBeltType,
                direction: existingBeltDirection,
              },
              to: { type: building.id, direction: currentState.playerFacing },
            });

            return true;
          }
        }
      }

      // Check if all tiles are suitable for building placement
      for (const pos of buildingPositions) {
        const posKey = `${pos.x},${pos.y}`;
        const currentTile = getTile
          ? getTile(
              pos.x,
              pos.y,
              currentState.gameMap,
              currentState.buildingLayer
            )
          : currentState.gameMap[posKey];

        // If still undefined, something is wrong with world generation
        if (currentTile === undefined) {
          showError?.('Unable to place building - world generation error');
          return false;
        }

        // Check if there's already a building at this position
        const existingBuilding = currentState.buildingLayer[posKey];
        if (existingBuilding) {
          showError?.('Cannot place building - position already occupied');
          return false;
        }

        const requiresResourceNode =
          building.category === 'mining' &&
          building.requiresResourceNode !== false;

        if (requiresResourceNode) {
          if (
            currentTile !== GAME_CONFIG.IRON_NODE_CHARACTER &&
            currentTile !== GAME_CONFIG.COPPER_NODE_CHARACTER
          ) {
            showError?.(
              `${building.name} can only be placed on mining nodes (I or C)`
            );
            return false;
          }
        }

        const isBlockedTerrain =
          currentTile === GAME_CONFIG.WALL_CHARACTER ||
          currentTile === GAME_CONFIG.TREE_CHARACTER ||
          currentTile === GAME_CONFIG.ROCK_CHARACTER ||
          currentTile === GAME_CONFIG.WATER_CHARACTER ||
          currentTile === GAME_CONFIG.MOUNTAIN_CHARACTER;

        if (isBlockedTerrain) {
          showError?.(`Cannot place ${building.name} on this terrain`);
          return false;
        }

        if (
          building.category !== 'mining' &&
          (currentTile === GAME_CONFIG.IRON_NODE_CHARACTER ||
            currentTile === GAME_CONFIG.COPPER_NODE_CHARACTER)
        ) {
          showError?.(`Cannot place ${building.name} on this terrain`);
          return false;
        }

        // Can't place on the player's position (except for belts)
        if (
          pos.x === currentState.playerPos.x &&
          pos.y === currentState.playerPos.y &&
          !isBelt
        ) {
          showError?.(`Cannot place ${building.name} on player position`);
          return false;
        }
      }

      if (!itemsReserved && !reserveItems()) {
        return false;
      }

      // Place the building in the building layer
      // For directional buildings like belts, use the appropriate directional character
      const buildingCharacter = getDirectionalBuildingCharacter(
        building,
        currentState.playerFacing
      );

      // Handle smart belt placement for transport belts
      if (isBelt) {
        const smartBeltResult = processSmartBeltPlacement(
          { x, y },
          building.id as BeltType,
          currentState.playerFacing,
          currentState.buildingLayer
        );

        // Use optimized direction if smart belt system suggests one
        const finalDirection =
          smartBeltResult.optimizedNewDirection || currentState.playerFacing;
        const finalBuildingCharacter = getDirectionalBuildingCharacter(
          building,
          finalDirection
        );

        // Update building layer with smart belt changes and add the new belt
        engine.setState({
          buildingLayer: {
            ...smartBeltResult.updatedBuildingLayer,
            [`${x},${y}`]: finalBuildingCharacter,
          },
        });

        if (playerInventoryChanged) {
          engine.setState({ playerInventory: playerInventoryAfterCost });
        }
        if (chestInventoryChanged) {
          engine.setState({
            chestInventoryLayer: chestInventoryLayerAfterCost,
          });
        }

        // Log smart belt connection if it happened
        if (
          smartBeltResult.previousBeltUpdated &&
          smartBeltResult.previousBeltPosition
        ) {
          buildingLogger.info('Smart belt connection made', {
            newBelt: { x, y, direction: finalDirection },
            previousBelt: {
              position: smartBeltResult.previousBeltPosition,
              oldDirection: smartBeltResult.previousBeltOldDirection,
              newDirection: smartBeltResult.previousBeltNewDirection,
            },
            optimizedDirection: smartBeltResult.optimizedNewDirection
              ? 'used'
              : 'not_needed',
          });
        }
      } else {
        // Regular building placement - place on all positions for multi-tile buildings
        const newBuildingLayer = { ...currentState.buildingLayer };
        for (const pos of buildingPositions) {
          const posKey = `${pos.x},${pos.y}`;
          newBuildingLayer[posKey] = buildingCharacter;
        }
        engine.setState({ buildingLayer: newBuildingLayer });

        if (playerInventoryChanged) {
          engine.setState({ playerInventory: playerInventoryAfterCost });
        }
        if (chestInventoryChanged) {
          engine.setState({
            chestInventoryLayer: chestInventoryLayerAfterCost,
          });
        }
      }

      // Initialize miner timer for mining buildings
      if (building.category === 'mining') {
        const currentTick = getCurrentTick();
        const newMinerTimerLayer = { ...currentState.minerTimerLayer };
        for (const pos of buildingPositions) {
          const posKey = `${pos.x},${pos.y}`;
          newMinerTimerLayer[posKey] = currentTick;
        }
        engine.setState({ minerTimerLayer: newMinerTimerLayer });
        buildingLogger.info('Initialized miner timer', {
          building: building.name,
          positions: buildingPositions.map(p => `${p.x},${p.y}`),
          startTick: currentTick,
        });
      }

      // Initialize assembler inventory for assemblers and factories
      if (
        building.id === BuildingType.ASSEMBLER ||
        building.id === BuildingType.FACTORY
      ) {
        // For multi-tile buildings, use the center position
        const centerX = x;
        const centerY = y;
        const posKey = `${centerX},${centerY}`;

        const newAssemblerInventoryLayer = {
          ...currentState.assemblerInventoryLayer,
          [posKey]: {
            items: createEmptyItemCounts(),
            recipe: null,
            craftingProgress: 0,
            craftingStartTick: null,
          },
        };
        engine.setState({
          assemblerInventoryLayer: newAssemblerInventoryLayer,
        });

        buildingLogger.info('Initialized assembler inventory', {
          building: building.name,
          position: posKey,
        });
      }

      if ((building.stackCapacity ?? 0) > 0) {
        const centerX = x;
        const centerY = y;
        const posKey = `${centerX},${centerY}`;

        chestInventoryLayerAfterCost = {
          ...chestInventoryLayerAfterCost,
          [posKey]: createEmptyChestInventory(),
        };
        chestInventoryChanged = true;

        buildingLogger.info('Initialized chest inventory', {
          building: building.name,
          position: posKey,
          stackCapacity: building.stackCapacity,
        });
      }

      if (chestInventoryChanged) {
        engine.setState({ chestInventoryLayer: chestInventoryLayerAfterCost });
      }

      return true;
    },

    upgradeAssemblerToFactory: (position: Position): UpgradeAssemblerResult => {
      return engine.upgradeAssemblerToFactory(position);
    },

    // Building deconstruction
    deconstructBuilding: (
      x: number,
      y: number,
      showError?: (message: string) => void
    ): boolean => {
      const currentState = engine.getState();

      // Get the building at the target position from the building layer
      const key = `${x},${y}`;
      const buildingCharacter = currentState.buildingLayer[key];

      if (!buildingCharacter) {
        showError?.('No building found at this position');
        return false;
      }

      // Find the building that matches this character (including directional characters)
      const building = findBuildingByCharacter(buildingCharacter);

      if (!building) {
        showError?.('Cannot deconstruct this tile');
        return false;
      }

      // Can't deconstruct on the player's position
      if (x === currentState.playerPos.x && y === currentState.playerPos.y) {
        showError?.('Cannot deconstruct building under player');
        return false;
      }

      // For multi-tile buildings, find the center position first
      let centerPosition: Position;
      let buildingPositions: Position[];

      if (
        building.size &&
        (building.size.width > 1 || building.size.height > 1)
      ) {
        // Multi-tile building - find the center from any clicked tile
        const foundCenter = findBuildingCenter(
          x,
          y,
          building,
          currentState.buildingLayer
        );
        if (!foundCenter) {
          showError?.('Cannot determine building center for deconstruction');
          return false;
        }
        centerPosition = foundCenter;
        buildingPositions = getBuildingPositions(
          centerPosition.x,
          centerPosition.y,
          building
        );
      } else {
        // Single-tile building
        centerPosition = { x, y };
        buildingPositions = [{ x, y }];
      }

      // Remove the building from the building layer (terrain remains unchanged)
      const newBuildingLayer = { ...currentState.buildingLayer };
      for (const pos of buildingPositions) {
        const posKey = `${pos.x},${pos.y}`;
        delete newBuildingLayer[posKey];
      }
      engine.setState({ buildingLayer: newBuildingLayer });

      // For crafting buildings, also remove the inventory data and rejection indicators
      if (
        building.id === BuildingType.ASSEMBLER ||
        building.id === BuildingType.FACTORY
      ) {
        const newInventoryLayer = { ...currentState.assemblerInventoryLayer };
        const centerKey = `${centerPosition.x},${centerPosition.y}`;
        delete newInventoryLayer[centerKey];
        engine.setState({ assemblerInventoryLayer: newInventoryLayer });

        // Remove any rejection indicators on all assembler tiles
        const newRejectedLayer = { ...currentState.rejectedItemsLayer };
        for (const pos of buildingPositions) {
          const posKey = `${pos.x},${pos.y}`;
          delete newRejectedLayer[posKey];
        }
        engine.setState({ rejectedItemsLayer: newRejectedLayer });
      }

      const refundedItems = createEmptyItemCounts();
      const itemRequirements = building.cost.items ?? [];
      for (const requirement of itemRequirements) {
        const added = engine.addItemToInventory(
          requirement.item,
          requirement.amount
        );
        refundedItems[requirement.item] = added;
        if (added < requirement.amount) {
          buildingLogger.warn('Inventory full while refunding items', {
            item: requirement.item,
            attempted: requirement.amount,
            added,
          });
        }
      }

      buildingLogger.info('Building deconstructed', {
        building: building.name,
        clickPosition: { x, y },
        centerPosition,
        tilesRemoved: buildingPositions.length,
        refundedItems,
      });

      // Don't show success toast for normal deconstruction - let the resource counter speak for itself
      return true;
    },

    // Biome tracking
    updateBiome: (biomeName: string): void => {
      engine.updateBiome(biomeName);
    },

    // Get target position based on facing direction
    getTargetPosition: (building?: Building): Position => {
      return engine.getTargetPosition(building);
    },

    // Get building target location (where building center should be placed)
    getBuildingTargetLocation: (building: Building): Position => {
      return engine.getBuildingTargetLocation(building);
    },

    // Zoom controls
    zoomIn: (): void => {
      engine.zoomIn();
    },

    zoomOut: (): void => {
      engine.zoomOut();
    },

    resetZoom: (): void => {
      engine.resetZoom();
    },

    // Save/Load
    saveGame,
    loadGameFromSaveData: (saveData: SaveData): void => {
      applySaveData(saveData);
      setIsLoading(false);
    },

    // Reset game state
    resetGame: (): void => {
      engine.resetGame();
      // Clear saved data
      localStorage.removeItem(SAVE_CONFIG.SAVE_KEY);
      setSpaceGalaxyState(calculateInitialGalaxyState());
    },

    launchRocket: (): void => {
      engine.launchRocket();
      saveGame();
    },

    // Set hasEnabledDebug flag
    setHasEnabledDebug: (enabled: boolean): void => {
      engine.setHasEnabledDebug(enabled);
    },

    setSpaceGalaxyState: (updater: SetStateAction<GalaxyState>): void => {
      setSpaceGalaxyState(updater);
    },

    // Achievement actions
    getAchievements: (): Achievement[] => {
      return engine.getAchievements();
    },

    triggerAchievementCheck: (context: AchievementContext): void => {
      engine.triggerAchievementCheck(context);
    },

    // Build menu state management
    setBuildMenuState: (updates: {
      buildMenuOpen?: BuildMenuState;
      selectedCategory?: BuildingCategory | null;
      selectedBuilding?: Building | null;
    }): void => {
      engine.setBuildMenuState(updates);
    },

    // Building status menu management
    setBuildingStatusMenuTarget: (
      target: { x: number; y: number } | null
    ): void => {
      engine.setBuildingStatusMenuTarget(target);
    },

    openBuildingStatusMenu: (position: { x: number; y: number }): void => {
      engine.openBuildingStatusMenu(position);
    },

    closeBuildingStatusMenu: (): void => {
      engine.closeBuildingStatusMenu();
    },

    openResourceNodeMenu: (position: { x: number; y: number }): void => {
      engine.openResourceNodeMenu(position);
    },

    closeResourceNodeMenu: (): void => {
      engine.closeResourceNodeMenu();
    },

    mineResourceNode: (position: {
      x: number;
      y: number;
    }): MineResourceResult => {
      return engine.mineResourceNode(position);
    },

    unlockResearch: (researchId: ResearchId): void => {
      engine.unlockResearch(researchId);
    },

    forceUnlockResearch: (researchId: ResearchId): void => {
      engine.forceUnlockResearch(researchId);
    },

    isResearchUnlocked: (researchId: ResearchId): boolean => {
      return engine.isResearchUnlocked(researchId);
    },

    getUnlockedResearchIds: (): ResearchId[] => {
      return serializeResearchState(engine.getResearchState());
    },

    selectResearch: (researchId: ResearchId | null): void => {
      engine.selectResearch(researchId);
    },

    setResearchMenuOpen: (open: boolean): void => {
      engine.setResearchMenuOpen(open);
    },

    setProductionMenuOpen: (open: boolean): void => {
      engine.setProductionMenuOpen(open);
    },

    resetResearch: (researchId: ResearchId): void => {
      engine.resetResearch(researchId);
    },

    resetAllResearch: (): void => {
      engine.resetAllResearch();
    },

    setTickSpeedMultiplier: (multiplier: number): void => {
      const engineState = engine.getState();
      if (!engineState.hasEnabledDebug) {
        engine.setTickSpeedMultiplier(1);
        return;
      }

      engine.setTickSpeedMultiplier(multiplier);
    },

    cycleTickSpeedMultiplier: (): number => {
      const engineState = engine.getState();
      if (!engineState.hasEnabledDebug) {
        engine.setTickSpeedMultiplier(1);
        return 1;
      }

      return engine.cycleTickSpeedMultiplier();
    },

    forcePrestige: (count: number): void => {
      engine.forcePrestige(count);
    },

    selectPrestigeUpgrade: (upgradeId: PrestigeUpgradeId | null): void => {
      engine.selectPrestigeUpgrade(upgradeId);
    },

    purchasePrestigeUpgrade: (upgradeId: PrestigeUpgradeId): boolean => {
      return engine.purchasePrestigeUpgrade(upgradeId);
    },

    setPrestigeMenuOpen: (open: boolean): void => {
      engine.setPrestigeMenuOpen(open);
    },
  };

  // Create additional React-compatible setters for compatibility
  const setGameMap = useCallback(
    (newMap: GameMap | ((prev: GameMap) => GameMap)): void => {
      // For now, we'll need to handle this case by case as the engine is updated
      // This is a transitional approach
      if (typeof newMap === 'function') {
        const currentMap = engine.getState().gameMap;
        const finalMap = newMap(currentMap);
        // For now, we'll update the engine's state directly
        engine.setState({ gameMap: finalMap });
      } else {
        engine.setState({ gameMap: newMap });
      }
    },
    [engine]
  );

  const setBuildingLayer = useCallback(
    (
      newLayer: BuildingLayer | ((prev: BuildingLayer) => BuildingLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().buildingLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ buildingLayer: finalLayer });
      } else {
        engine.setState({ buildingLayer: newLayer });
      }
    },
    [engine]
  );

  const setBeltContentLayer = useCallback(
    (
      newLayer:
        | BeltContentLayer
        | ((prev: BeltContentLayer) => BeltContentLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().beltContentLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ beltContentLayer: finalLayer });
      } else {
        engine.setState({ beltContentLayer: newLayer });
      }
    },
    [engine]
  );

  const setAssemblerInventoryLayer = useCallback(
    (
      newLayer:
        | AssemblerInventoryLayer
        | ((prev: AssemblerInventoryLayer) => AssemblerInventoryLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().assemblerInventoryLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ assemblerInventoryLayer: finalLayer });
      } else {
        engine.setState({ assemblerInventoryLayer: newLayer });
      }
    },
    [engine]
  );

  const setChestInventoryLayer = useCallback(
    (
      newLayer:
        | ChestInventoryLayer
        | ((prev: ChestInventoryLayer) => ChestInventoryLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().chestInventoryLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ chestInventoryLayer: finalLayer });
      } else {
        engine.setState({ chestInventoryLayer: newLayer });
      }
    },
    [engine]
  );

  const setRejectedItemsLayer = useCallback(
    (
      newLayer:
        | RejectedItemsLayer
        | ((prev: RejectedItemsLayer) => RejectedItemsLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().rejectedItemsLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ rejectedItemsLayer: finalLayer });
      } else {
        engine.setState({ rejectedItemsLayer: newLayer });
      }
    },
    [engine]
  );

  const setMinerTimerLayer = useCallback(
    (
      newLayer: MinerTimerLayer | ((prev: MinerTimerLayer) => MinerTimerLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().minerTimerLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ minerTimerLayer: finalLayer });
      } else {
        engine.setState({ minerTimerLayer: newLayer });
      }
    },
    [engine]
  );

  const setMinerOutputBufferLayer = useCallback(
    (
      newLayer:
        | MinerOutputBufferLayer
        | ((prev: MinerOutputBufferLayer) => MinerOutputBufferLayer)
    ): void => {
      if (typeof newLayer === 'function') {
        const currentLayer = engine.getState().minerOutputBufferLayer;
        const finalLayer = newLayer(currentLayer);
        engine.setState({ minerOutputBufferLayer: finalLayer });
      } else {
        engine.setState({ minerOutputBufferLayer: newLayer });
      }
    },
    [engine]
  );

  const setCurrentBiome = useCallback(
    (newBiome: string | ((prev: string) => string)): void => {
      if (typeof newBiome === 'function') {
        const currentBiome = engine.getState().currentBiome;
        const finalBiome = newBiome(currentBiome);
        engine.updateBiome(finalBiome);
      } else {
        engine.updateBiome(newBiome);
      }
    },
    [engine]
  );

  const setPlayerFacing = useCallback(
    (
      newFacing: PlayerFacing | ((prev: PlayerFacing) => PlayerFacing)
    ): void => {
      if (typeof newFacing === 'function') {
        const currentFacing = engine.getState().playerFacing;
        const finalFacing = newFacing(currentFacing);
        engine.setState({ playerFacing: finalFacing });
      } else {
        engine.setState({ playerFacing: newFacing });
      }
    },
    [engine]
  );

  const setZoomLevel = useCallback(
    (newZoom: number | ((prev: number) => number)): void => {
      if (typeof newZoom === 'function') {
        const currentZoom = engine.getState().zoomLevel;
        const finalZoom = newZoom(currentZoom);
        engine.setState({ zoomLevel: finalZoom });
      } else {
        engine.setState({ zoomLevel: newZoom });
      }
    },
    [engine]
  );

  return {
    // State - now from engine
    isLoading,
    playerPos: engineState.playerPos,
    playerInventory: engineState.playerInventory,
    gameMap: engineState.gameMap,
    buildingLayer: engineState.buildingLayer,
    beltContentLayer: engineState.beltContentLayer,
    assemblerInventoryLayer: engineState.assemblerInventoryLayer,
    chestInventoryLayer: engineState.chestInventoryLayer,
    rejectedItemsLayer: engineState.rejectedItemsLayer,
    minerTimerLayer: engineState.minerTimerLayer,
    minerOutputBufferLayer: engineState.minerOutputBufferLayer,
    prestigePoints: engineState.prestigePoints,
    prestigeCount: engineState.prestigeCount,
    cameraPos: engineState.cameraPos,
    currentBiome: engineState.currentBiome,
    playerFacing: engineState.playerFacing,
    zoomLevel: engineState.zoomLevel,
    tickSpeedMultiplier: engineState.tickSpeedMultiplier,
    researchState: engineState.researchState,
    powerProduction: engineState.powerProduction,
    powerDemand: engineState.powerDemand,
    powerSatisfaction: engineState.powerSatisfaction,
    powerHistory: engineState.powerHistory,
    powerDemandByBuildingType: engineState.powerDemandByBuildingType,
    productionMenuOpen: engineState.productionMenuOpen,
    itemProductionHistory: engineState.itemProductionHistory,
    currentTick: engineState.currentTick,
    handCraftingQueue: engineState.handCraftingQueue,
    spaceGalaxyState,
    prestigeUpgradeState: engineState.prestigeUpgradeState,
    prestigeMenuOpen: engineState.prestigeMenuOpen,
    quickBeltPreview: engineState.quickBeltPreview,
    // Build menu state from engine
    buildMenuOpen: engineState.buildMenuOpen,
    selectedCategory: engineState.selectedCategory,
    selectedBuilding: engineState.selectedBuilding,
    // UI menu states from engine
    helpMenuOpen: engineState.helpMenuOpen,
    saveMenuOpen: engineState.saveMenuOpen,
    leftPanelOpen: engineState.leftPanelOpen,
    debugMenuOpen: engineState.debugMenuOpen,
    achievementMenuOpen: engineState.achievementMenuOpen,
    researchMenuOpen: engineState.researchMenuOpen,
    buildingStatusMenuTarget: engineState.buildingStatusMenuTarget,
    resourceNodeMenu: engineState.resourceNodeMenu,
    hasEnabledDebug: engineState.hasEnabledDebug,
    achievements: engineState.achievements || [],
    // Actions
    ...actions,
    placeBuilding: actions.placeBuilding,
    deconstructBuilding: actions.deconstructBuilding,
    upgradeAssemblerToFactory: actions.upgradeAssemblerToFactory,
    // Raw setters (for compatibility with existing code)
    setGameMap,
    setBuildingLayer,
    setBeltContentLayer,
    setAssemblerInventoryLayer,
    setChestInventoryLayer,
    setRejectedItemsLayer,
    setMinerTimerLayer,
    setMinerOutputBufferLayer,
    setCurrentBiome,
    setPlayerFacing,
    setZoomLevel,

    // GameEngine's tile lookup method - single source of truth
    getTile: (
      x: number,
      y: number,
      gameMap?: GameMap,
      buildingLayer?: BuildingLayer
    ) => engine.getTile(x, y, gameMap, buildingLayer),
  };
};

// Get the appropriate character for directional buildings (belts)
export const getDirectionalBuildingCharacter = (
  building: Building,
  facing: PlayerFacing
): string => {
  // Handle transport belts with directional characters
  if (building.id === BuildingType.BELT) {
    return BELT_CHARACTERS[facing];
  } else if (building.id === BuildingType.FAST_BELT) {
    return FAST_BELT_CHARACTERS[facing];
  }

  // For non-directional buildings, return the default character
  return building.character;
};

// Find building by character, including directional characters
export const findBuildingByCharacter = (character: string): Building | null => {
  // First check if it's a standard building character
  const standardBuilding = Object.values(BUILDINGS).find(
    b => b.character === character
  );
  if (standardBuilding) {
    return standardBuilding;
  }

  // Check if it's a directional belt character
  const beltDirections = Object.values(BELT_CHARACTERS) as string[];
  if (beltDirections.includes(character)) {
    return BUILDINGS.BELT;
  }

  const fastBeltDirections = Object.values(FAST_BELT_CHARACTERS) as string[];
  if (fastBeltDirections.includes(character)) {
    return BUILDINGS.FAST_BELT;
  }

  if (character === BUILDINGS.UNDERGROUND_BELT.character) {
    return BUILDINGS.UNDERGROUND_BELT;
  }

  return null;
};

// Helper functions for multi-tile buildings
export const getBuildingSize = (
  building: Building
): { width: number; height: number } => {
  return building.size || { width: 1, height: 1 };
};

// Get all positions occupied by a multi-tile building
export const getBuildingPositions = (
  centerX: number,
  centerY: number,
  building: Building
): Position[] => {
  const size = getBuildingSize(building);
  const positions: Position[] = [];

  // Calculate offset from center to top-left corner
  const offsetX = Math.floor(size.width / 2);
  const offsetY = Math.floor(size.height / 2);

  for (let dy = 0; dy < size.height; dy++) {
    for (let dx = 0; dx < size.width; dx++) {
      positions.push({
        x: centerX - offsetX + dx,
        y: centerY - offsetY + dy,
      });
    }
  }

  return positions;
};

// Helper function to find a building at a position (including multi-tile building detection)
const findBuildingFromPosition = (
  x: number,
  y: number,
  buildingLayer: BuildingLayer
): { building: Building | null; centerPosition: Position | null } => {
  // Check if there's a building at the exact position
  const posKey = `${x},${y}`;
  const buildingChar = buildingLayer[posKey];

  if (buildingChar) {
    const building = findBuildingByCharacter(buildingChar);
    if (building) {
      return { building, centerPosition: { x, y } };
    }
  }

  // If no building at exact position, check if this position is within a multi-tile building's footprint
  for (let centerY = y - 2; centerY <= y + 2; centerY++) {
    for (let centerX = x - 2; centerX <= x + 2; centerX++) {
      const centerKey = `${centerX},${centerY}`;
      const centerBuilding = buildingLayer[centerKey];
      if (centerBuilding) {
        const building = findBuildingByCharacter(centerBuilding);
        if (
          building &&
          building.size &&
          (building.size.width > 1 || building.size.height > 1)
        ) {
          // Check if current position is within this building's footprint
          const buildingPositions = getBuildingPositions(
            centerX,
            centerY,
            building
          );
          const isWithinFootprint = buildingPositions.some(
            pos => pos.x === x && pos.y === y
          );
          if (isWithinFootprint) {
            return { building, centerPosition: { x: centerX, y: centerY } };
          }
        }
      }
    }
  }

  return { building: null, centerPosition: null };
};

// Find the center position of a multi-tile building from any of its occupied tiles
export const findBuildingCenter = (
  x: number,
  y: number,
  building: Building,
  buildingLayer: BuildingLayer
): Position | null => {
  const result = findBuildingFromPosition(x, y, buildingLayer);
  // Verify the found building matches the expected building type
  if (result.building && result.building.id === building.id) {
    return result.centerPosition;
  }
  return null;
};

// Centralized building lookup that handles multi-tile buildings properly
export const getBuildingAtPosition = (
  x: number,
  y: number,
  buildingLayer: BuildingLayer
): { building: Building | null; centerPosition: Position | null } => {
  return findBuildingFromPosition(x, y, buildingLayer);
};

export const getMaxInputOutputsMultiplier = (
  position: Position,
  buildingLayer: BuildingLayer,
  researchState?: ResearchState
): number => {
  const { building } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );
  const baseMultiplier = building?.maxInputOutputsMultiplier ?? 1;
  const researchMultiplier = researchState
    ? getOutputCapacityMultiplier(researchState)
    : 1;
  return baseMultiplier * researchMultiplier;
};

export const getTotalChestItemCounts = (
  chestInventoryLayer: ChestInventoryLayer
): Record<ItemType, number> => {
  const totals = createEmptyItemCounts();

  Object.values(chestInventoryLayer).forEach(inventory => {
    if (!inventory) {
      return;
    }

    ITEM_TYPES.forEach(itemType => {
      const count = inventory.items[itemType] ?? 0;
      if (count > 0) {
        totals[itemType] += count;
      }
    });
  });

  return totals;
};

export const getPlayerInventoryItemCounts = (
  inventory: PlayerInventory
): Record<ItemType, number> => {
  const totals = createEmptyItemCounts();
  const normalized = normalizePlayerInventory(inventory);

  normalized.forEach(slot => {
    if (slot.itemType && slot.count > 0) {
      totals[slot.itemType] += slot.count;
    }
  });

  return totals;
};

export const getAvailableItemCountsForPlacement = (
  inventory: PlayerInventory,
  chestInventoryLayer: ChestInventoryLayer
): Record<ItemType, number> => {
  const available = createEmptyItemCounts();
  const playerCounts = getPlayerInventoryItemCounts(inventory);
  const chestCounts = getTotalChestItemCounts(chestInventoryLayer);

  ITEM_TYPES.forEach(itemType => {
    available[itemType] = playerCounts[itemType] + chestCounts[itemType];
  });

  return available;
};

export const removeItemsFromPlayerInventory = (
  inventory: PlayerInventory,
  itemType: ItemType,
  amount: number
): { inventory: PlayerInventory; removed: number } => {
  if (amount <= 0) {
    return { inventory, removed: 0 };
  }

  const normalized = normalizePlayerInventory(inventory);
  const updatedSlots = normalized.map(slot => ({ ...slot }));
  let remaining = amount;

  for (const slot of updatedSlots) {
    if (remaining <= 0) {
      break;
    }

    if (slot.itemType !== itemType || slot.count <= 0) {
      continue;
    }

    const toRemove = Math.min(slot.count, remaining);
    slot.count -= toRemove;
    remaining -= toRemove;
    if (slot.count <= 0) {
      slot.itemType = null;
      slot.count = 0;
    }
  }

  const removed = amount - remaining;
  const normalizedSlots: PlayerInventory = updatedSlots.map(slot =>
    slot.count > 0
      ? { itemType: slot.itemType, count: slot.count }
      : { itemType: null, count: 0 }
  );

  return { inventory: normalizedSlots, removed };
};

export const consumeItemsForBuilding = (
  inventory: PlayerInventory,
  chestInventoryLayer: ChestInventoryLayer,
  requirements: readonly BuildingItemRequirement[]
): {
  success: boolean;
  playerInventory: PlayerInventory;
  chestInventoryLayer: ChestInventoryLayer;
  playerInventoryChanged: boolean;
  chestInventoryChanged: boolean;
} => {
  if (requirements.length === 0) {
    return {
      success: true,
      playerInventory: inventory,
      chestInventoryLayer,
      playerInventoryChanged: false,
      chestInventoryChanged: false,
    };
  }

  const availableTotals = getAvailableItemCountsForPlacement(
    inventory,
    chestInventoryLayer
  );

  for (const requirement of requirements) {
    const available = availableTotals[requirement.item] ?? 0;
    if (available < requirement.amount) {
      return {
        success: false,
        playerInventory: inventory,
        chestInventoryLayer,
        playerInventoryChanged: false,
        chestInventoryChanged: false,
      };
    }
  }

  let updatedInventory = inventory;
  let updatedChestLayer = chestInventoryLayer;
  let playerInventoryChanged = false;
  let chestInventoryChanged = false;

  for (const requirement of requirements) {
    let remaining = requirement.amount;

    if (remaining <= 0) {
      continue;
    }

    const removalResult = removeItemsFromPlayerInventory(
      updatedInventory,
      requirement.item,
      remaining
    );
    updatedInventory = removalResult.inventory;
    remaining -= removalResult.removed;
    if (removalResult.removed > 0) {
      playerInventoryChanged = true;
    }

    if (remaining > 0) {
      const chestResult = consumeItemsFromChests(updatedChestLayer, [
        { item: requirement.item, amount: remaining },
      ]);

      if (!chestResult.success) {
        return {
          success: false,
          playerInventory: inventory,
          chestInventoryLayer,
          playerInventoryChanged: false,
          chestInventoryChanged: false,
        };
      }

      updatedChestLayer = chestResult.layer;
      chestInventoryChanged = true;
    }
  }

  return {
    success: true,
    playerInventory: normalizePlayerInventory(updatedInventory),
    chestInventoryLayer: updatedChestLayer,
    playerInventoryChanged,
    chestInventoryChanged,
  };
};

export const consumeItemsFromChests = (
  chestInventoryLayer: ChestInventoryLayer,
  requirements: readonly BuildingItemRequirement[]
): { success: boolean; layer: ChestInventoryLayer } => {
  if (requirements.length === 0) {
    return { success: true, layer: chestInventoryLayer };
  }

  const availableTotals = getTotalChestItemCounts(chestInventoryLayer);
  for (const requirement of requirements) {
    const available = availableTotals[requirement.item] ?? 0;
    if (available < requirement.amount) {
      return { success: false, layer: chestInventoryLayer };
    }
  }

  const updatedLayer: ChestInventoryLayer = { ...chestInventoryLayer };
  const modifiedChests = new Map<string, ChestInventory>();
  const chestKeys = Object.keys(updatedLayer);

  for (const requirement of requirements) {
    let remaining = requirement.amount;

    for (const key of chestKeys) {
      if (remaining <= 0) {
        break;
      }

      const existingInventory = updatedLayer[key];
      if (!existingInventory) {
        continue;
      }

      const currentCount = existingInventory.items[requirement.item] ?? 0;
      if (currentCount <= 0) {
        continue;
      }

      const toRemove = Math.min(currentCount, remaining);
      let mutableInventory = modifiedChests.get(key);
      if (!mutableInventory) {
        mutableInventory = {
          items: { ...existingInventory.items },
        };
        modifiedChests.set(key, mutableInventory);
      }

      mutableInventory.items[requirement.item] = Math.max(
        0,
        (mutableInventory.items[requirement.item] ?? 0) - toRemove
      );
      remaining -= toRemove;
    }

    if (remaining > 0) {
      return { success: false, layer: chestInventoryLayer };
    }
  }

  modifiedChests.forEach((inventory, key) => {
    const normalizedItems = { ...inventory.items };
    ITEM_TYPES.forEach(itemType => {
      const value = normalizedItems[itemType] ?? 0;
      normalizedItems[itemType] = value > 0 ? value : 0;
    });
    updatedLayer[key] = { items: normalizedItems };
  });

  return { success: true, layer: updatedLayer };
};

const calculateChestStacksUsed = (items: Record<ItemType, number>): number => {
  return ITEM_TYPES.reduce((total, itemType) => {
    const count = items[itemType] || 0;
    if (count <= 0) {
      return total;
    }

    const stackSize = getItemStackSize(itemType);
    return total + Math.ceil(count / stackSize);
  }, 0);
};

export const createEmptyPlayerInventory = (): PlayerInventory =>
  Array.from({ length: PLAYER_INVENTORY_SLOT_COUNT }, () => ({
    itemType: null,
    count: 0,
  }));

export const normalizePlayerInventory = (
  inventory?: PlayerInventory
): PlayerInventory => {
  const empty = createEmptyPlayerInventory();
  if (!inventory || inventory.length === 0) {
    return empty;
  }

  return empty.map((_, index) => {
    const source = inventory[index];
    if (!source) {
      return { itemType: null, count: 0 };
    }

    const { itemType, count } = source;
    if (!itemType || count <= 0) {
      return { itemType: null, count: 0 };
    }

    if (!ITEM_TYPES.includes(itemType)) {
      return { itemType: null, count: 0 };
    }

    const stackSize = getItemStackSize(itemType);
    const normalizedCount = Math.min(stackSize, count);

    return {
      itemType,
      count: normalizedCount,
    };
  });
};

export const addItemToPlayerInventory = (
  inventory: PlayerInventory,
  itemType: ItemType,
  amount: number = 1
): { inventory: PlayerInventory; added: number } => {
  if (amount <= 0) {
    return { inventory, added: 0 };
  }

  const normalized = normalizePlayerInventory(inventory);
  const stackSize = getItemStackSize(itemType);
  const updatedSlots = normalized.map(slot => ({ ...slot }));
  let remaining = amount;

  for (const slot of updatedSlots) {
    if (remaining <= 0) {
      break;
    }

    if (slot.itemType !== itemType) {
      continue;
    }

    const capacity = stackSize - slot.count;
    if (capacity <= 0) {
      continue;
    }

    const toAdd = Math.min(capacity, remaining);
    slot.count += toAdd;
    remaining -= toAdd;
  }

  for (const slot of updatedSlots) {
    if (remaining <= 0) {
      break;
    }

    if (slot.itemType !== null && slot.count > 0) {
      continue;
    }

    const toAdd = Math.min(stackSize, remaining);
    slot.itemType = itemType;
    slot.count = toAdd;
    remaining -= toAdd;
  }

  const added = amount - remaining;
  if (added === 0) {
    return { inventory, added: 0 };
  }

  const normalizedSlots: PlayerInventory = updatedSlots.map(slot =>
    slot.count > 0
      ? { itemType: slot.itemType, count: slot.count }
      : { itemType: null, count: 0 }
  );

  return {
    inventory: normalizedSlots,
    added,
  };
};

export const createEmptyChestInventory = (): ChestInventory => ({
  items: createEmptyItemCounts(),
});

export const getChestStackCapacity = (
  position: Position,
  buildingLayer: BuildingLayer
): number => {
  const { building } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );

  return building?.stackCapacity ?? 0;
};

export const getChestInventory = (
  chestInventoryLayer: ChestInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position
): ChestInventory | null => {
  const { building, centerPosition } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );

  if (!building || !centerPosition || !building.stackCapacity) {
    return null;
  }

  const key = `${centerPosition.x},${centerPosition.y}`;
  return chestInventoryLayer[key] || null;
};

export const setChestInventory = (
  chestInventoryLayer: ChestInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position,
  inventory: ChestInventory
): ChestInventoryLayer => {
  const { building, centerPosition } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );

  if (!building || !centerPosition || !building.stackCapacity) {
    return chestInventoryLayer;
  }

  const key = `${centerPosition.x},${centerPosition.y}`;
  return {
    ...chestInventoryLayer,
    [key]: inventory,
  };
};

export const addItemsToChest = (
  chestInventoryLayer: ChestInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position,
  itemType: ItemType,
  amount: number = 1
): { layer: ChestInventoryLayer; added: number } => {
  if (amount <= 0) {
    return { layer: chestInventoryLayer, added: 0 };
  }

  const { building, centerPosition } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );

  if (!building || !centerPosition || !building.stackCapacity) {
    return { layer: chestInventoryLayer, added: 0 };
  }

  const key = `${centerPosition.x},${centerPosition.y}`;
  const existingInventory =
    chestInventoryLayer[key] || createEmptyChestInventory();
  const currentItems = existingInventory.items;
  const stackSize = getItemStackSize(itemType);
  const currentItemCount = currentItems[itemType] || 0;
  const currentStacksForItem = currentItemCount
    ? Math.ceil(currentItemCount / stackSize)
    : 0;

  const currentStacksUsed = calculateChestStacksUsed(currentItems);
  const maxStacksForItem = Math.max(
    0,
    building.stackCapacity - (currentStacksUsed - currentStacksForItem)
  );

  if (maxStacksForItem <= 0) {
    return { layer: chestInventoryLayer, added: 0 };
  }

  const maxItemCount = maxStacksForItem * stackSize;
  const availableItems = Math.max(0, maxItemCount - currentItemCount);

  if (availableItems <= 0) {
    return { layer: chestInventoryLayer, added: 0 };
  }

  const itemsToAdd = Math.min(amount, availableItems);
  const updatedItems = {
    ...currentItems,
    [itemType]: currentItemCount + itemsToAdd,
  };

  return {
    layer: {
      ...chestInventoryLayer,
      [key]: {
        items: updatedItems,
      },
    },
    added: itemsToAdd,
  };
};

// Get the target position for multi-tile buildings (centered in front of player)
export const getMultiTileBuildingTargetPosition = (
  playerPos: Position,
  playerFacing: PlayerFacing,
  building: Building
): Position => {
  const size = getBuildingSize(building);

  // For 1x1 buildings, use the original logic
  if (size.width === 1 && size.height === 1) {
    switch (playerFacing) {
      case 'up':
        return { x: playerPos.x, y: playerPos.y - 1 };
      case 'down':
        return { x: playerPos.x, y: playerPos.y + 1 };
      case 'left':
        return { x: playerPos.x - 1, y: playerPos.y };
      case 'right':
        return { x: playerPos.x + 1, y: playerPos.y };
      default:
        return { x: playerPos.x, y: playerPos.y + 1 };
    }
  }

  // For multi-tile buildings, calculate center position in front of player
  const centerOffset = Math.ceil(size.width / 2);

  switch (playerFacing) {
    case 'up':
      return { x: playerPos.x, y: playerPos.y - centerOffset };
    case 'down':
      return { x: playerPos.x, y: playerPos.y + centerOffset };
    case 'left':
      return { x: playerPos.x - centerOffset, y: playerPos.y };
    case 'right':
      return { x: playerPos.x + centerOffset, y: playerPos.y };
    default:
      return { x: playerPos.x, y: playerPos.y + centerOffset };
  }
};

// Helper functions for assembler inventory management
export const setAssemblerRecipe = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position,
  recipe: Recipe | null,
  researchState: ResearchState
): AssemblerInventoryLayer => {
  const centerPosition = getAssemblerCenterPosition(position, buildingLayer);
  if (!centerPosition) {
    return assemblerInventoryLayer;
  }

  const posKey = `${centerPosition.x},${centerPosition.y}`;

  if (recipe === null) {
    // Clear recipe and inventory
    const newLayer = { ...assemblerInventoryLayer };
    delete newLayer[posKey];
    return newLayer;
  }

  const { building } = getBuildingAtPosition(
    centerPosition.x,
    centerPosition.y,
    buildingLayer
  );

  if (!building) {
    return assemblerInventoryLayer;
  }

  if (!isRecipeUnlocked(researchState, recipe.id)) {
    buildingLogger.warn('Recipe selection blocked by research lock', {
      position: posKey,
      recipe: recipe.id,
    });
    return assemblerInventoryLayer;
  }

  // Some tests (and potentially legacy save data) provide recipes without an
  // explicit craftingBuilding. Treat those as assembler recipes so the recipe
  // check remains permissive while still enforcing factory-only recipes.
  const craftingBuilding = recipe.craftingBuilding ?? BuildingType.ASSEMBLER;

  const isFactory = building.id === BuildingType.FACTORY;
  const isAssembler = building.id === BuildingType.ASSEMBLER;
  const recipeRequiresFactory = craftingBuilding === BuildingType.FACTORY;
  const recipeRequiresAssembler = craftingBuilding === BuildingType.ASSEMBLER;

  const canUseRecipe =
    (isAssembler && recipeRequiresAssembler) ||
    (isFactory && recipeRequiresFactory);

  if (!canUseRecipe) {
    buildingLogger.warn('Recipe selection blocked by incompatible building', {
      position: posKey,
      recipe: recipe.id,
      building: building.id,
    });
    return assemblerInventoryLayer;
  }

  // Set recipe and initialize empty inventory
  const emptyInventory = createEmptyItemCounts();

  return {
    ...assemblerInventoryLayer,
    [posKey]: {
      items: emptyInventory,
      recipe,
      craftingProgress: 0,
      craftingStartTick: null,
    },
  };
};

export const getAssemblerInventory = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position
): AssemblerInventory | null => {
  const centerPosition = getAssemblerCenterPosition(position, buildingLayer);
  if (!centerPosition) {
    return null;
  }

  const centerKey = `${centerPosition.x},${centerPosition.y}`;
  return assemblerInventoryLayer[centerKey] || null;
};

// Set assembler inventory by finding the building center first
export const setAssemblerInventory = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position,
  inventory: AssemblerInventory
): AssemblerInventoryLayer => {
  const centerPosition = getAssemblerCenterPosition(position, buildingLayer);
  if (!centerPosition) {
    return assemblerInventoryLayer;
  }

  const centerKey = `${centerPosition.x},${centerPosition.y}`;
  return {
    ...assemblerInventoryLayer,
    [centerKey]: inventory,
  };
};

export const addItemToAssembler = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  position: Position,
  itemType: ItemType,
  count: number = 1,
  researchState?: ResearchState
): AssemblerInventoryLayer => {
  const centerPosition = getAssemblerCenterPosition(position, buildingLayer);
  if (!centerPosition) {
    return assemblerInventoryLayer;
  }

  const posKey = `${centerPosition.x},${centerPosition.y}`;
  const currentInventory = assemblerInventoryLayer[posKey];

  buildingLogger.debug('Attempting to add item to assembler', {
    position: posKey,
    itemType,
    count,
    hasInventory: !!currentInventory,
    recipe: currentInventory?.recipe?.name || 'none',
  });

  if (!currentInventory) {
    buildingLogger.debug('No assembler inventory found at position', {
      position: posKey,
    });
    return assemblerInventoryLayer; // No assembler at this position
  }

  // Check if item is needed for the current recipe
  const recipe = currentInventory.recipe;
  if (!recipe) {
    buildingLogger.debug('No recipe set for assembler', { position: posKey });
    return assemblerInventoryLayer; // No recipe set
  }

  const isNeededForRecipe = recipe.inputs.some(
    input => input.item === itemType
  );
  if (!isNeededForRecipe) {
    buildingLogger.debug('Item not needed for recipe', {
      position: posKey,
      itemType,
      recipeInputs: recipe.inputs.map(i => i.item),
    });
    return assemblerInventoryLayer; // Item not needed for recipe
  }

  const maxInputOutputsMultiplier = getMaxInputOutputsMultiplier(
    centerPosition,
    buildingLayer,
    researchState
  );
  const recipeInput = recipe.inputs.find(input => input.item === itemType);
  const currentCount = currentInventory.items[itemType] || 0;
  const maxCapacity = (recipeInput?.count || 0) * maxInputOutputsMultiplier;
  const spaceAvailable = Math.max(0, maxCapacity - currentCount);

  if (!recipeInput || spaceAvailable <= 0) {
    buildingLogger.debug('Assembler input capacity reached', {
      position: posKey,
      itemType,
      currentCount,
      maxCapacity,
    });
    return assemblerInventoryLayer;
  }

  const amountToAdd = Math.min(count, spaceAvailable);

  return {
    ...assemblerInventoryLayer,
    [posKey]: {
      ...currentInventory,
      items: {
        ...currentInventory.items,
        [itemType]: currentCount + amountToAdd,
      },
    },
  };
};

// Helper functions for game state logic
export const isWalkable = (
  tile: string | undefined,
  debugState?: DebugState
): boolean => {
  // If collision is disabled in debug mode, everything is walkable
  if (debugState?.collisionDisabled) {
    return true;
  }

  return (
    tile !== GAME_CONFIG.WALL_CHARACTER &&
    tile !== GAME_CONFIG.TREE_CHARACTER &&
    tile !== GAME_CONFIG.ROCK_CHARACTER &&
    tile !== GAME_CONFIG.WATER_CHARACTER &&
    tile !== GAME_CONFIG.MOUNTAIN_CHARACTER &&
    tile !== GAME_CONFIG.IRON_NODE_CHARACTER &&
    tile !== GAME_CONFIG.COPPER_NODE_CHARACTER
  );
};

// Check if a building is walkable (can pass through)
export const isWalkableBuilding = (
  buildingChar: string | undefined,
  debugState?: DebugState
): boolean => {
  // If collision is disabled in debug mode, can walk through all buildings
  if (debugState?.collisionDisabled) {
    return true;
  }

  if (!buildingChar) {
    return true; // No building = walkable
  }

  const building = findBuildingByCharacter(buildingChar);
  if (!building) {
    return true; // Unknown building = walkable
  }

  // Large buildings are solid - cannot walk through
  if (
    building.id === BuildingType.ASSEMBLER ||
    building.id === BuildingType.FACTORY
  ) {
    return false;
  }

  // All other buildings are walkable (belts, miners, etc.)
  return true;
};

export const validateMovement = (newX: number, newY: number): boolean => {
  return (
    newX >= 1 &&
    newX <= GAME_CONFIG.WORLD_WIDTH - 2 &&
    newY >= 1 &&
    newY <= GAME_CONFIG.WORLD_HEIGHT - 2
  );
};

// Save game state to localStorage
export const saveGameState = (
  gameState: Omit<SaveData, 'version' | 'timestamp'>
): boolean => {
  try {
    const saveData: SaveData = {
      version: SAVE_CONFIG.VERSION,
      timestamp: Date.now(),
      playerPos: gameState.playerPos,
      playerInventory: normalizePlayerInventory(gameState.playerInventory),
      gameMap: gameState.gameMap,
      buildingLayer: gameState.buildingLayer,
      beltContentLayer: gameState.beltContentLayer,
      assemblerInventoryLayer: gameState.assemblerInventoryLayer,
      chestInventoryLayer: gameState.chestInventoryLayer,
      rejectedItemsLayer: gameState.rejectedItemsLayer,
      minerTimerLayer: gameState.minerTimerLayer,
      minerOutputBufferLayer: gameState.minerOutputBufferLayer,
      prestigePoints: gameState.prestigePoints,
      prestigeCount: gameState.prestigeCount,
      cameraPos: gameState.cameraPos,
      currentBiome: gameState.currentBiome,
      playerFacing: gameState.playerFacing,
      zoomLevel: gameState.zoomLevel,
      buildMenuOpen: gameState.buildMenuOpen,
      selectedCategory: gameState.selectedCategory,
      selectedBuilding: gameState.selectedBuilding,
      helpMenuOpen: gameState.helpMenuOpen,
      saveMenuOpen: gameState.saveMenuOpen,
      leftPanelOpen: gameState.leftPanelOpen,
      debugMenuOpen: gameState.debugMenuOpen,
      achievementMenuOpen: gameState.achievementMenuOpen,
      researchMenuOpen: gameState.researchMenuOpen,
      hasEnabledDebug: gameState.hasEnabledDebug,
      achievements: gameState.achievements,
      unlockedResearch: gameState.unlockedResearch || [],
      selectedResearch: gameState.selectedResearch ?? null,
      researchProgress: gameState.researchProgress ?? {},
      powerProduction: gameState.powerProduction ?? 0,
      powerDemand: gameState.powerDemand ?? 0,
      powerSatisfaction: gameState.powerSatisfaction ?? 1,
      powerHistory: gameState.powerHistory ?? [],
      powerDemandByBuildingType: gameState.powerDemandByBuildingType ?? {},
      spaceGalaxyState:
        gameState.spaceGalaxyState ?? calculateInitialGalaxyState(),
      handCraftingQueue: gameState.handCraftingQueue ?? [],
      ...(gameState.hasEnabledDebug &&
      gameState.tickSpeedMultiplier !== undefined
        ? { tickSpeedMultiplier: gameState.tickSpeedMultiplier }
        : {}),
      ...(gameState.repeatableResearchLevels &&
      Object.keys(gameState.repeatableResearchLevels).length > 0
        ? { repeatableResearchLevels: gameState.repeatableResearchLevels }
        : {}),
      ...(gameState.prestigeUpgradeState
        ? { prestigeUpgradeState: gameState.prestigeUpgradeState }
        : {}),
    };

    localStorage.setItem(SAVE_CONFIG.SAVE_KEY, JSON.stringify(saveData));
    saveLogger.info('Game saved successfully', {
      playerPosition: saveData.playerPos,
      timestamp: saveData.timestamp,
    });
    return true;
  } catch (error) {
    saveLogger.error('Failed to save game', { error });
    return false;
  }
};

// Load game state from localStorage
export const loadGameState = (): SaveData | null => {
  try {
    const savedData = localStorage.getItem(SAVE_CONFIG.SAVE_KEY);
    if (!savedData) {
      return null;
    }

    const saveData = JSON.parse(savedData) as SaveData;

    // Version check
    if (saveData.version !== SAVE_CONFIG.VERSION) {
      saveLogger.warn(
        'Save data version mismatch, clearing incompatible save',
        {
          expected: SAVE_CONFIG.VERSION,
          found: saveData.version,
        }
      );

      // Show user-friendly prompt for incompatible saves
      const shouldClear = window.confirm(
        `Your save file (version ${saveData.version}) is incompatible with this version of the game (${SAVE_CONFIG.VERSION}).\n\n` +
          'This is due to new layered building system improvements.\n\n' +
          'Would you like to clear the old save data and start fresh?\n\n' +
          'Click OK to start a new game, or Cancel to keep the incompatible save (may cause issues).'
      );

      if (shouldClear) {
        // Clear the incompatible save
        localStorage.removeItem(SAVE_CONFIG.SAVE_KEY);
        saveLogger.info('Cleared incompatible save data at user request');
      }

      return null;
    }

    saveLogger.info('Game loaded successfully', {
      playerPosition: saveData.playerPos,
      worldSize: Object.keys(saveData.gameMap).length,
    });
    const normalizedUnlockedResearch = Array.isArray(saveData.unlockedResearch)
      ? saveData.unlockedResearch
      : [];

    if (!Array.isArray(saveData.unlockedResearch)) {
      saveLogger.warn(
        'Invalid unlocked research data, resetting to empty list'
      );
    }

    return {
      ...saveData,
      playerInventory: normalizePlayerInventory(saveData.playerInventory),
      unlockedResearch: normalizedUnlockedResearch,
      selectedResearch: saveData.selectedResearch ?? null,
      researchProgress: saveData.researchProgress ?? {},
      minerOutputBufferLayer: saveData.minerOutputBufferLayer ?? {},
      tickSpeedMultiplier:
        saveData.hasEnabledDebug && saveData.tickSpeedMultiplier !== undefined
          ? Math.max(1, Math.floor(saveData.tickSpeedMultiplier))
          : 1,
      spaceGalaxyState:
        saveData.spaceGalaxyState ?? calculateInitialGalaxyState(),
    };
  } catch (error) {
    saveLogger.error('Failed to load game', { error });
    return null;
  }
};
